import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { AnimatePresence, motion } from "motion/react";
import {
  MDSButton,
  MDSIcon,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSTextOverflow,
} from "@/mds";
import { MDSIconKind } from "@/mds";
import { useAppStore } from "@/store";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { requestStartHuddleMode } from "@/domains/huddle/huddle-mode-events";
import { checkIfIsOnboarding, getSendSuggestions } from "./empty-overlay.utils";

interface Props {
  currentSessionId: string;
  isClosed?: boolean;
}

export const EmptyOverlay = observer(({ currentSessionId, isClosed = false }: Props) => {
  const { pageStore, store } = useAppStore();
  const memItStore = pageStore.memItPage;

  const overlayRef = useRef<HTMLDivElement>(null);
  const resumeRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const lastResumeSizeRef = useRef<{ width: number; height: number } | null>(null);
  const [hideResumeDueToCollision, setHideResumeDueToCollision] = useState(false);

  const showEmptyState = memItStore.sessionEvents.length === 0 && !isClosed;
  const [showResumeButton, setShowResumeButton] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSessionId, setRecentSessionId] = useState<string | undefined>();

  useEffect(() => {
    if (!showEmptyState) {
      setShowResumeButton(false);
      setShowSuggestions(false);
      setRecentSessionId(undefined);
      return;
    }

    const recent = memItStore.previousChatSessions[0];
    if (recent && recent.modelId !== currentSessionId) {
      setShowResumeButton(memItStore.sessionEvents.length === 0);
      setShowSuggestions(memItStore.sessionEvents.length === 0);
      setRecentSessionId(recent.modelId);
      return;
    }

    if (!recent) {
      setShowResumeButton(false);
      setShowSuggestions(true);
      setRecentSessionId(undefined);
      return;
    }

    setShowResumeButton(false);
    setShowSuggestions(false);
    setRecentSessionId(undefined);
  }, [currentSessionId, showEmptyState, memItStore, memItStore.previousChatSessions]);

  const handleResumePastSession = () => {
    if (recentSessionId) {
      memItStore.handleSelectSession(recentSessionId);
    }
  };

  const handleSendSuggestion = useCallback(
    async ({ message, title, subtitle }: { title: string; subtitle?: string; message: string }) => {
      trackEvent(TrackedEvent.ChatMessageSendFromSuggestion, { message, title, subtitle });
      await memItStore.handleCreateUserMessage({ content: message, context: {} });
    },
    [memItStore]
  );

  const handleStartHuddleFromSuggestion = useCallback(() => {
    requestStartHuddleMode();
  }, []);

  // Detect overlap between floating resume and suggestions containers and hide resume if needed
  const checkCollision = useCallback(() => {
    if (!overlayRef.current) {
      return;
    }

    const containerRect = overlayRef.current.getBoundingClientRect();

    // Determine resume rect from live node if present; otherwise synthesize using last known size
    let resumeRect: DOMRect | { left: number; right: number; top: number; bottom: number } | null =
      null;

    if (resumeRef.current) {
      const liveRect = resumeRef.current.getBoundingClientRect();
      // cache last known size for when the node is unmounted
      lastResumeSizeRef.current = { width: liveRect.width, height: liveRect.height };
      resumeRect = liveRect;
    } else if (lastResumeSizeRef.current) {
      const { width, height } = lastResumeSizeRef.current;
      // FloatingResumeContainer styles: top: 80px; left: 50%; transform: translateX(-50%)
      const top = containerRect.top + 80;
      const centerX = containerRect.left + containerRect.width / 2;
      const left = centerX - width / 2;
      resumeRect = { left, right: left + width, top, bottom: top + height };
    }

    // If we still can't determine resume rect, don't toggle state to avoid flicker
    if (!resumeRect || !suggestionsRef.current) {
      return;
    }

    const suggestionsRect = suggestionsRef.current.getBoundingClientRect();

    // Explicit overlap-only check (touching edges does NOT count as overlap)
    const rectanglesOverlap = !(
      resumeRect.right <= suggestionsRect.left ||
      resumeRect.left >= suggestionsRect.right ||
      resumeRect.bottom <= suggestionsRect.top ||
      resumeRect.top >= suggestionsRect.bottom
    );

    setHideResumeDueToCollision(rectanglesOverlap);
  }, []);

  useLayoutEffect(() => {
    if (!showSuggestions) {
      setHideResumeDueToCollision(false);
      return;
    }

    // Run after layout for accurate measurements
    const raf = requestAnimationFrame(checkCollision);

    const handleWindowResize = () => requestAnimationFrame(checkCollision);
    window.addEventListener("resize", handleWindowResize);

    const resizeObserver: ResizeObserver | undefined = new ResizeObserver(() => {
      requestAnimationFrame(checkCollision);
    });
    if (overlayRef.current) {
      resizeObserver.observe(overlayRef.current);
    }
    if (suggestionsRef.current) {
      resizeObserver.observe(suggestionsRef.current);
    }
    if (resumeRef.current) {
      resizeObserver.observe(resumeRef.current);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", handleWindowResize);
      resizeObserver?.disconnect();
    };
  }, [showSuggestions, checkCollision]);

  const showOverlay = showResumeButton || showSuggestions;
  const isOnboarding = checkIfIsOnboarding({ store });
  const shouldUseHuddleEmptyState =
    !isOnboarding && (store.spaceAccountFeatureFlags.getFlagStatus("huddle-mode") ?? false);
  const suggestions = getSendSuggestions({
    isOnboarding,
    store,
    useHuddleSuggestions: shouldUseHuddleEmptyState,
  });

  return (
    <OverlayRoot ref={overlayRef} data-component="EmptyOverlay">
      {showOverlay && (
        <>
          <AnimatePresence initial={false}>
            {!hideResumeDueToCollision && showResumeButton && (
              <motion.div
                key="resume-fab"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ pointerEvents: "auto" }}
              >
                <FloatingResumeContainer ref={resumeRef} data-component="FloatingResumeContainer">
                  <MDSButton
                    variant={MDSButtonVariant.Transparent}
                    onClick={handleResumePastSession}
                  >
                    <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.ArrowUp} />
                    Resume last chat
                  </MDSButton>
                </FloatingResumeContainer>
              </motion.div>
            )}
          </AnimatePresence>
          {showSuggestions && (
            <FloatingSuggestionsContainer
              ref={suggestionsRef}
              data-huddle-layout={shouldUseHuddleEmptyState ? "true" : undefined}
              data-component="FloatingSuggestionsContainer"
            >
              {shouldUseHuddleEmptyState ? (
                <EmptyStateTitle
                  variant={MDSTextVariant.HeaderS}
                  weight={MDSTextWeight.SemiBold}
                  overflow={MDSTextOverflow.Ellipsis}
                >
                  How can I help?
                </EmptyStateTitle>
              ) : (
                <MDSText
                  variant={MDSTextVariant.HeaderS}
                  weight={MDSTextWeight.SemiBold}
                  overflow={MDSTextOverflow.Ellipsis}
                  style={{ width: "100%", textAlign: "center", marginBottom: "8px" }}
                >
                  {!isOnboarding
                    ? "Ask about your notes"
                    : `Welcome to Mem, ${store.account.myAccount.profileDisplayName}`}
                </MDSText>
              )}
              <StyledSuggestions
                data-huddle-layout={shouldUseHuddleEmptyState ? "true" : undefined}
                isOnboarding={isOnboarding}
              >
                {suggestions.map(({ title, subtitle, message }, index) => {
                  const isHuddleEntry = shouldUseHuddleEmptyState && index === 0;
                  const iconKind = isHuddleEntry ? MDSIconKind.Phone : MDSIconKind.Message;

                  if (!shouldUseHuddleEmptyState) {
                    return (
                      <StyledButton
                        key={index}
                        variant={MDSButtonVariant.ContrastAccent}
                        onClick={() => handleSendSuggestion({ message, title, subtitle })}
                      >
                        <StyledButtonContent>
                          <SuggestionTitle>{title}</SuggestionTitle>
                          {subtitle && <SuggestionSubtitle>{subtitle}</SuggestionSubtitle>}
                        </StyledButtonContent>
                      </StyledButton>
                    );
                  }

                  return (
                    <StyledButton
                      key={index}
                      data-huddle-layout="true"
                      variant={MDSButtonVariant.ContrastAccent}
                      onPress={
                        isHuddleEntry
                          ? handleStartHuddleFromSuggestion
                          : () => handleSendSuggestion({ message, title, subtitle })
                      }
                    >
                      <SuggestionIcon slot={MDSButtonSlotName.LeftIcon} kind={iconKind} />
                      <StyledButtonContent data-huddle-layout="true">
                        <SuggestionTitle data-huddle-layout="true">{title}</SuggestionTitle>
                        {subtitle && <SuggestionSubtitle>{subtitle}</SuggestionSubtitle>}
                      </StyledButtonContent>
                    </StyledButton>
                  );
                })}
              </StyledSuggestions>
            </FloatingSuggestionsContainer>
          )}
        </>
      )}
    </OverlayRoot>
  );
});

const EmptyStateTitle = styled(MDSText)`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing["3"]};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 30px;
  line-height: 36px;
  letter-spacing: 0;
  text-align: center;
`;

// TODO: upgrade MDSButton to support 100% with and 2 lines when experiment is over
const StyledButton = styled(MDSButton)`
  height: auto;
  min-height: auto;
  border-radius: 100px;

  &[data-huddle-layout="true"] {
    width: 100%;
    min-height: 44px;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing["2"]};
    padding: 10px ${({ theme }) => theme.spacing["4"]};
    background: ${({ theme }) => theme.colors.surface.pane};
  }

  &[data-huddle-layout="true"][data-hovered]:not([data-disabled]) {
    background: ${({ theme }) => theme.colors.surface.pane};
    transform: translateY(-1px);
  }

  &[data-huddle-layout="true"] .mds-icon {
    color: ${({ theme }) => theme.colors.icon.secondary};
    height: 18px;
    width: 18px;
  }
`;

const StyledSuggestions = styled.div<{ isOnboarding: boolean }>`
  width: ${({ isOnboarding }) => (isOnboarding ? "100%" : "auto")};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["5"]};

  &[data-huddle-layout="true"] {
    width: min(100%, 624px);
    gap: ${({ theme }) => theme.spacing["3"]};
  }
`;

const StyledButtonContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  height: auto;
  min-height: auto;
  align-items: center;
  text-align: center;
  white-space: normal;
  word-wrap: break-word;

  &[data-huddle-layout="true"] {
    gap: ${({ theme }) => theme.spacing["1"]};
  }
`;

const SuggestionTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["text-sm"]};
  font-weight: ${({ theme }) => theme.fontWeights.smedium};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};

  &[data-huddle-layout="true"] {
    font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  }
`;

const SuggestionSubtitle = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
`;

const SuggestionIcon = styled(MDSIcon)``;

const OverlayRoot = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
`;

const FloatingResumeContainer = styled.div({
  position: "absolute",
  top: "50px",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 1,
  pointerEvents: "auto",
});

const FloatingSuggestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
  align-items: center;

  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  pointer-events: auto;
  width: 100%;
  padding: 0 ${({ theme }) => theme.spacing["2"]};

  &[data-huddle-layout="true"] {
    gap: ${({ theme }) => theme.spacing["4"]};
    padding: 0 clamp(24px, 5vw, 40px);
  }
`;

export default EmptyOverlay;
